import React, { useState, useEffect } from "react";
import { Header } from "../components/Header";
import { EnhancedHero } from "../components/EnhancedHero";
import { EventGrid } from "../components/EventGrid";
import { TrustBanner } from "../components/TrustBanner";
import { CreateEventsPage } from "../components/CreateEventsPage";
import { HelpPage } from "../components/HelpPage";
import { PrivacyPolicyPage } from "../components/PrivacyPolicyPage";
import { CookiePolicyPage } from "../components/CookiePolicyPage";
import { MobileFooter } from "../components/MobileFooter";

import { Footer } from "../components/Footer";
import { BackToTop } from "../components/BackToTop";
import { Toaster } from "../components/Toaster";
import { DemoIndicator } from "../components/DemoIndicator";

// Motion UI Components
import { MotionBackground } from "../components/MotionBackground";
import {
  ScrollRevealWrapper,
  StaggeredAnimation,
} from "../components/ScrollRevealWrapper";
import { ParallaxContainer } from "../components/ParallaxContainer";
import { FloatingElements } from "../components/FloatingElements";
import {
  ProgressIndicator,
  ReadingProgressIndicator,
} from "../components/ProgressIndicator";
import { InteractiveElements } from "../components/InteractiveElements";

export default function App() {
  // LIGHT MODE IS NOW DEFAULT
  const [darkMode, setDarkMode] = useState(false);
  
  // Initialize current page based on URL path
  const getInitialPage = () => {
    const path = window.location.pathname;
    if (path === '/produtor') return 'produtor';
    if (path === '/ajuda') return 'ajuda';
    if (path === '/politica-privacidade') return 'politica-privacidade';
    if (path === '/politica-cookies') return 'politica-cookies';
    return 'home';
  };
  
  const [currentPage, setCurrentPage] = useState(getInitialPage());

  // Initialize with dark mode as default, only use light mode if explicitly set
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    
    // Dark mode is default unless explicitly set to light
    if (savedTheme === "light") {
      setDarkMode(false);
      document.documentElement.classList.remove("dark");
    } else {
      // Default to dark mode
      setDarkMode(true);
      document.documentElement.classList.add("dark");
      
      // If no theme is saved, save dark as the preference
      if (!savedTheme) {
        localStorage.setItem("theme", "dark");
      }
    }

    // Handle browser back/forward navigation
    const handlePopState = () => {
      setCurrentPage(getInitialPage());
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Set favicon
  useEffect(() => {
    const favicon =
      document.querySelector('link[rel="icon"]') ||
      document.createElement("link");
    favicon.rel = "icon";
    favicon.type = "image/png";
    favicon.href =
      "https://uniqueingressos.com.br/assets/img/favicon.png";

    if (!document.querySelector('link[rel="icon"]')) {
      document.head.appendChild(favicon);
    }

    // Also set apple-touch-icon for better mobile support
    const appleTouchIcon =
      document.querySelector('link[rel="apple-touch-icon"]') ||
      document.createElement("link");
    appleTouchIcon.rel = "apple-touch-icon";
    appleTouchIcon.href =
      "https://uniqueingressos.com.br/assets/img/favicon.png";

    if (
      !document.querySelector('link[rel="apple-touch-icon"]')
    ) {
      document.head.appendChild(appleTouchIcon);
    }

    // Set document title based on current page
    const pageTitles = {
      home: "Unique Ingressos – A tecnologia por trás dos maiores eventos",
      "produtor": "Criar Eventos – Unique Ingressos",
      "ajuda": "Central de Ajuda – Unique Ingressos",
      "politica-privacidade": "Política de Privacidade – Unique Ingressos",
      "politica-cookies": "Política de Cookies – Unique Ingressos",
    };

    document.title =
      pageTitles[currentPage] || pageTitles["home"];
  }, [currentPage]);

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);

    if (newDarkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  };

  const handleNavigation = (page: string) => {
    // If user clicks "my-tickets", redirect to external login URL in same tab
    if (page === "my-tickets") {
        window.location.href = `${import.meta.env.VITE_REDIRECT_URL}/login`;
      return;
    }

    setCurrentPage(page);
    
    // Update URL without page reload
    const urlPath = page === 'home' ? '/' : `/${page}`;
    window.history.pushState({}, '', urlPath);

    // Scroll to top when navigating
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isHomePage = currentPage === "home";

  const renderCurrentPage = () => {
    switch (currentPage) {
      case "produtor":
        return <CreateEventsPage onNavigate={handleNavigation} />;
      case "ajuda":
        return <HelpPage />;
      case "politica-privacidade":
        return (
          <PrivacyPolicyPage onNavigate={handleNavigation} />
        );
      case "politica-cookies":
        return (
          <CookiePolicyPage onNavigate={handleNavigation} />
        );
      case "home":
      default:
        return (
          <main className="relative">
            {/* Enhanced Hero Section with Motion UI - Altura Balanceada */}
            <ScrollRevealWrapper
              animation="fadeInUp"
              duration={800}
            >
              <ParallaxContainer
                speed={0.3}
                className="relative"
              >
                <EnhancedHero />
              </ParallaxContainer>
            </ScrollRevealWrapper>

            {/* Main Events Grid with Direct Animation - SMALL BOTTOM SPACING ON MOBILE */}
            <div className="relative -mt-6 mobile-event-section-with-margin">
              <div className="animate-fade-in-up" style={{ animationDelay: '200ms', animationFillMode: 'both' }}>
                <ParallaxContainer speed={0.1}>
                  <EventGrid />
                </ParallaxContainer>
              </div>
            </div>

            {/* Trust Banner with Reveal Animation - Desktop Only */}
            <div className="trust-banner-desktop-only">
              <ScrollRevealWrapper
                animation="fadeInUp"
                delay={400}
              >
                <div className="trust-banner-spacing">
                  <TrustBanner />
                </div>
              </ScrollRevealWrapper>
            </div>
          </main>
        );
    }
  };

  const shouldShowMotionUI = isHomePage;

  // Define which pages should show the footer
  const shouldShowFooter =
    isHomePage ||
    [
      "produtor",
      "ajuda",
      "politica-privacidade",
      "politica-cookies",
    ].includes(currentPage);

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-x-hidden mobile-app-container">
      {/* Motion UI Background - Only on Home Page */}
      {shouldShowMotionUI && (
        <>
          <MotionBackground
            variant="hero"
            showOrbs={true}
            showWaves={true}
            showMesh={true}
          />
          <FloatingElements density="medium" />
        </>
      )}

      {/* Header */}
      <Header
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        currentPage={currentPage}
        onNavigate={handleNavigation}
        isLoggedIn={false}
      />

      {/* Page Content with Specific Mobile Handling */}
      <div className={`relative z-10 ${isHomePage ? 'mobile-homepage-wrapper' : 'mobile-content-container'}`}>
        {renderCurrentPage()}
      </div>

      {/* UPDATED MOBILE SPACING FIX - CSS com margem pequena no mobile */}
      <style dangerouslySetInnerHTML={{
        __html: `
          /* Desktop Footer: Show on screens 640px+ */
          .footer-desktop {
            display: none;
          }
          
          @media (min-width: 640px) {
            .footer-desktop {
              display: block !important;
            }
          }

          /* Mobile Footer: Show on screens below 640px */
          .footer-mobile {
            display: block;
          }
          
          @media (min-width: 640px) {
            .footer-mobile {
              display: none !important;
            }
          }

          /* Trust Banner: Hide completely on mobile, show on desktop */
          .trust-banner-desktop-only {
            display: none !important;
          }
          
          @media (min-width: 640px) {
            .trust-banner-desktop-only {
              display: block !important;
            }
          }

          /* Trust Banner Spacing: Remove margin on mobile, add on desktop */
          .trust-banner-spacing {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }
          
          @media (min-width: 640px) {
            .trust-banner-spacing {
              margin-top: 1.5rem !important;
            }
          }

          /* UPDATED MOBILE HOMEPAGE FIX - COM PEQUENA MARGEM */
          @media (max-width: 639px) {
            /* Homepage wrapper - mobile footer padding + small content margin */
            .mobile-homepage-wrapper {
              padding-bottom: calc(80px + 1rem) !important;
            }
            
            /* EventGrid section - ADD small bottom margin on mobile */
            .mobile-event-section-with-margin {
              margin: 0 !important;
              padding: 0 !important;
              margin-bottom: 1rem !important; /* Small margin between EventGrid and mobile footer */
              padding-bottom: 0 !important;
            }
            
            /* Allow the EventGrid component to have its natural bottom spacing */
            .mobile-event-section-with-margin section {
              padding-bottom: 0.5rem !important; /* Small padding within EventGrid itself */
              margin-bottom: 0 !important;
            }
            
            /* Override specific EventGrid section classes but allow small spacing */
            .mobile-event-section-with-margin section[class*="pt-6"],
            .mobile-event-section-with-margin section[class*="pb-8"] {
              padding-bottom: 0.5rem !important; /* Instead of 0, allow small padding */
            }
            
            /* Reset only the excessive nested spacing, keep container margins */
            .mobile-event-section-with-margin section > *,
            .mobile-event-section-with-margin section > * > *,
            .mobile-event-section-with-margin section > * > * > *,
            .mobile-event-section-with-margin section > * > * > * > * {
              margin-bottom: 0 !important;
              padding-bottom: 0 !important;
            }
            
            /* Override any ScrollRevealWrapper or ParallaxContainer spacing but keep section margin */
            .mobile-event-section-with-margin [class*="scroll"],
            .mobile-event-section-with-margin [class*="parallax"],
            .mobile-event-section-with-margin [class*="reveal"],
            .mobile-event-section-with-margin [class*="container"] {
              margin-bottom: 0 !important;
              padding-bottom: 0 !important;
            }
          }

          /* Desktop: Keep zero bottom spacing for design consistency */
          @media (min-width: 640px) {
            .mobile-event-section-with-margin {
              margin-bottom: 0 !important;
              padding-bottom: 0 !important;
            }
            
            .mobile-event-section-with-margin section {
              padding-bottom: 0 !important;
              margin-bottom: 0 !important;
            }
          }

          /* Reset mobile-content-container for other pages */
          .mobile-content-container {
            padding-bottom: 0;
          }
          
          @media (max-width: 639px) {
            .mobile-content-container {
              padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important;
            }
          }
        `
      }} />

      {/* Desktop Footer */}
      {shouldShowFooter && (
        <div className="footer-desktop">
          <ScrollRevealWrapper animation="fadeInUp" delay={600}>
            <Footer onNavigate={handleNavigation} />
          </ScrollRevealWrapper>
        </div>
      )}

      {/* Mobile Footer */}
      {shouldShowFooter && (
        <div className="footer-mobile">
          <MobileFooter 
            currentPage={currentPage}
            onNavigate={handleNavigation}
            darkMode={darkMode}
          />
        </div>
      )}

      {/* Floating Components - Hide on mobile to avoid conflicts */}
      <div style={{ display: 'none' }}>
        <style dangerouslySetInnerHTML={{
          __html: `
            .floating-components-desktop {
              display: none !important;
            }
            
            @media (min-width: 640px) {
              .floating-components-desktop {
                display: block !important;
              }
            }
          `
        }} />
      </div>
      <div className="floating-components-desktop">
        <BackToTop />
      </div>

      {/* Motion UI Interactive Elements - Only on Home Page */}
      {shouldShowMotionUI && (
        <>
          <InteractiveElements
            enableCursor={false} // Disable custom cursor for better accessibility
            enableParticles={true}
            enableHoverEffects={true}
          />
          <ProgressIndicator
            position="top"
            thickness={3}
            showPercentage={false}
          />
          <ReadingProgressIndicator />
        </>
      )}

      {/* Toast Notifications */}
      <Toaster position="bottom-right" />

      {/* Demo/Environment Indicator */}
      <DemoIndicator />
    </div>
  );
}